import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { ArrowUpRight, ChevronDown, Search } from 'lucide-react';
import './SeitenRahmen.css';

// Gemeinsamer Seitenrahmen (Profil-Nachbau F-02, F-07, F-11 und F-19, Bennys Freigaben 14.10.2026; Vorbild meinGPT
// /account/profile, Bilder 35 bis 37): links eine Leiste mit Suchfeld, Gruppen und unten dem Bereichsnamen, rechts der Inhalt.
// Seit F-26 steht „Zurück zum Chat“ nicht mehr in der Leiste; das Fenster drumherum mit role="dialog", Schließen-Knopf und
// Tab-Fang baut components/BereichsSeite.tsx, der Rahmen selbst bleibt ohne Portal und ohne Dialogrolle. Der
// Rahmen kennt weder Router-Ebenen noch Rechte – Einträge, offenen Eintrag, Ziele, Sichtbarkeit und den Link-Baustein
// liefert der Aufrufer (Meine Einstellungen, Einstellungen der Firma, Plattform-Verwaltung, …). Gescrollt wird je nach
// Aufrufer im Inhalt (scroll="inhalt", Seite über der App) oder mit dem Dokument (scroll="dokument"; die Leiste klebt dann
// in voller Höhe). Unter 800 px ist die Leiste für alle Bereiche gleich zugeklappt über dem Inhalt (F-19): ein Knopf mit
// dem offenen Eintrag klappt sie auf, ein Wechsel oder Escape klappt sie zu.

export type RahmenEintrag = {
  key: string;
  titel: string;
  to: string;
  /** Symbol vor dem Titel (16 px, aria-hidden); ein Unter-Eintrag steht ohne Symbol. */
  symbol?: LucideIcon;
  /** Der offene Eintrag (aria-current="page"); entscheidet der Aufrufer, etwa nach Pfad und Anker. */
  aktiv?: boolean;
  /** false blendet den Eintrag aus (Rechte); ohne Angabe ist er sichtbar. */
  sichtbar?: boolean;
  /** Ziel außerhalb des Rahmens: gewöhnlicher Link mit Pfeil. Er läuft nicht über verweis, also auch ohne dessen
   *  Rückfrage bei ungespeicherten Eingaben. */
  extern?: boolean;
  /** Eingerückt und ohne Symbol unter dem Eintrag davor. */
  unter?: boolean;
  /** Weitere Suchwörter für titelFilter. */
  stichwoerter?: string[];
  /** Treffer über ein Stichwort; steht klein unter dem Titel (titelFilter setzt gefundenText). */
  fund?: string;
  /** Kleine Marke nach dem Titel, etwa bei Problemen; label ist der Text für Vorlesehilfen (PF-411-2). Ohne Angabe oder mit
   *  wert 0 keine Marke. */
  zaehler?: { wert: number; label: string };
  /** Profil-Nachbau F-22 (Standard F-22-2): ein eigenes Bedienelement statt des Links, etwa „Benachrichtigungen“ in der Leiste
   *  der Plattform-Verwaltung. Es steht als Eintrag in seiner Gruppe; Suche und Sichtbarkeit gelten wie für jeden Eintrag, aktiv
   *  und fund zeigt es nicht. */
  element?: React.ReactNode;
};
/** Profil-Nachbau F-26, Nachtrag (19.10.2026): ein Abschnitt der Leiste nach Berechtigung („Mein Konto“, „Firma“, „Plattform“ im
 *  Fenster „Einstellungen“). Die erste Gruppe eines Abschnitts trägt seine Überschrift; heißt sie selbst wie der Abschnitt, steht
 *  ihr Titel nicht noch einmal da. */
export type RahmenAbschnitt = { key: string; titel: string };
/** Eine Gruppe ohne Titel steht durch eine Linie abgesetzt. zusatz steht unter dem Titel (etwa Laden oder Fehler); eine
 *  Gruppe mit zusatz bleibt auch ohne sichtbaren Eintrag stehen. */
export type RahmenGruppe = { key: string; titel?: string | null; untertitel?: string; eintraege: RahmenEintrag[]; zusatz?: React.ReactNode; abschnitt?: RahmenAbschnitt };
/** Link-Baustein des Aufrufers: ein Link (Konto-Seite: EinstLink mit Rückfrage) oder ein Knopf; Vorgabe RouterVerweis. */
export type VerweisProps = { to: string; className?: string; 'aria-current'?: 'page'; onClick?: React.MouseEventHandler<HTMLElement>; children?: React.ReactNode };
export type Verweis = React.ComponentType<VerweisProps>;
export const RouterVerweis: Verweis = props => <Link {...props} />;
export type RahmenSuche = {
  /** Name des Felds für Vorlesehilfen und Platzhalter. */
  label: string;
  platzhalter: string;
  /** Die Gruppen zum Suchtext (getrimmt, nie leer); ohne Angabe titelFilter über gruppen. */
  filtern?: (text: string) => RahmenGruppe[];
  /** Satz ohne Treffer (steht immer im DOM, leer ohne Platz). */
  leerText: string;
  /** Weg ohne Treffer, etwa in die Hilfe. */
  leerWeg?: React.ReactNode;
};
export type SeitenRahmenProps = {
  /** Zusätzliche Klassen an der Wurzel (eigener Stilbereich, etwa „administration“). */
  klasse?: string;
  /** F-19, F-26: Escape auf der Seite ruft beiEscape (der Aufrufer setzt seine Rückfrage davor und schließt dann). Nicht,
   *  solange ein Dialog darüber offen ist (das Fenster um den Rahmen zählt nicht), das Suchfeld Text hat, die schmale Leiste
   *  aufgeklappt ist, die Seite inert ist oder ein anderes Element Escape schon verbraucht hat. */
  beiEscape?: () => void;
  suche?: RahmenSuche;
  /** Unter dem Suchfeld, über den Gruppen (etwa „Sicht wechseln“ in der Plattform-Verwaltung). */
  leisteZusatz?: React.ReactNode;
  /** Name der einen Navigation für Vorlesehilfen. */
  leisteName: string;
  gruppen: RahmenGruppe[];
  verweis?: Verweis;
  /** F-19: Bereichsname klein unten in der Leiste. */
  bereichsName?: string;
  /** Unten in der Leiste nach dem Bereichsnamen (etwa das Kontomenü); bei kurzer Leiste am unteren Rand. */
  leisteFuss?: React.ReactNode;
  /** main (Vorgabe, mit inhaltName) oder div, wenn der Aufrufer sein eigenes <main> mitbringt. */
  inhaltAls?: 'main' | 'div';
  /** Name des Hauptbereichs für Vorlesehilfen. */
  inhaltName?: string;
  inhaltKlasse?: string;
  inhaltRef?: React.Ref<HTMLElement>;
  /** Wo gescrollt wird: im Inhalt (Vorgabe) oder mit dem Dokument. */
  scroll?: 'inhalt' | 'dokument';
  /** Unter dem Inhalt (etwa die Rückfrage „Eingaben verwerfen?“). */
  unten?: React.ReactNode;
  children?: React.ReactNode;
};

const SCHMAL = '(max-width: 799px)';
/** Ein offener Dialog über der Seite behält Escape für sich (wie dialogDarueber im Einstellungen-Pop-up); ein Dialog in der
 *  inerten App dahinter zählt nicht, ebenso wenig das Fenster, in dem der Rahmen selbst steht (F-26, BereichsSeite.tsx). */
const dialogOffen = (rahmen: HTMLElement | null) => [...document.querySelectorAll('[role="dialog"], [role="alertdialog"], dialog[open], .editorial-modal-backdrop')]
  .some(element => element.getClientRects().length > 0 && !element.closest('[inert]') && !(rahmen && element.contains(rahmen)));

/** Vergleichsform: ohne Groß-/Kleinschreibung und Umlautunterschied („ae“ wie „ä“), „ß“ wie „ss“. */
export const vergleichsForm = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('de')
  .replace(/ß/g, 'ss').replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u');
/** Wortlaut eines Treffers über ein Stichwort (wie SUCHE.gefunden der Einstellungen). */
export const gefundenText = (wort: string) => `gefunden: „${wort}“`;

/** Einfache Suche: Einträge, deren Titel oder Stichwort den Text enthält (ein Stichwort-Treffer trägt fund); Gruppen ohne
 *  Treffer entfallen. */
export function titelFilter(gruppen: RahmenGruppe[], text: string): RahmenGruppe[] {
  const muster = vergleichsForm(text.trim());
  if (!muster) return gruppen;
  return gruppen.map(gruppe => ({ ...gruppe, eintraege: gruppe.eintraege.flatMap(eintrag => {
    if (vergleichsForm(eintrag.titel).includes(muster)) return [eintrag];
    const wort = eintrag.stichwoerter?.find(w => vergleichsForm(w).includes(muster));
    return wort ? [{ ...eintrag, fund: gefundenText(wort) }] : [];
  }) })).filter(gruppe => gruppe.eintraege.length > 0);
}

/** Die sichtbaren Gruppen: Einträge mit sichtbar === false entfallen, ebenso Gruppen ohne Eintrag und ohne zusatz. */
export const sichtbareGruppen = (gruppen: RahmenGruppe[]) => gruppen
  .map(gruppe => ({ ...gruppe, eintraege: gruppe.eintraege.filter(eintrag => eintrag.sichtbar !== false) }))
  .filter(gruppe => gruppe.eintraege.length > 0 || !!gruppe.zusatz);

/** Die Gruppen der Leiste als die eine Navigation; auch ohne Rahmen nutzbar. Jeder Eintrag (außer extern) wird mit verweis
 *  gerendert. */
export function LeistenGruppen({ gruppen, name, verweis = RouterVerweis }: { gruppen: RahmenGruppe[]; name: string; verweis?: Verweis }) {
  const id = useId();
  const V = verweis;
  return <nav aria-label={name} className="seiten-rahmen-nav">
    {gruppen.map((gruppe, index) => {
      const titelId = `${id}-${gruppe.key}`;
      // F-26, Nachtrag: die erste Gruppe eines Abschnitts trägt dessen Überschrift; heißt sie wie der Abschnitt, nur einmal.
      const abschnitt = gruppe.abschnitt && gruppe.abschnitt.key !== gruppen[index - 1]?.abschnitt?.key ? gruppe.abschnitt : null;
      const abschnittId = abschnitt ? `${id}-abschnitt-${abschnitt.key}` : undefined;
      const titel = abschnitt && gruppe.titel === abschnitt.titel ? null : gruppe.titel;
      const benannt = titel ? titelId : abschnitt ? abschnittId : undefined;
      const klasse = ['seiten-rahmen-gruppe', !titel && !abschnitt && 'seiten-rahmen-gruppe--ohne-titel', abschnitt && index > 0 && 'seiten-rahmen-gruppe--abschnitt'].filter(Boolean).join(' ');
      return <div key={gruppe.key} className={klasse} data-abschnitt={gruppe.abschnitt?.key}>
        {abschnitt && <p className="seiten-rahmen-abschnitt" id={abschnittId}>{abschnitt.titel}</p>}
        {titel && <p className="seiten-rahmen-gruppe-titel" id={titelId}>{titel}</p>}
        {gruppe.untertitel && <p className="seiten-rahmen-gruppe-untertitel">{gruppe.untertitel}</p>}
        {gruppe.zusatz}
        {gruppe.eintraege.length > 0 && <ul aria-labelledby={benannt}>{gruppe.eintraege.map(eintrag => {
          const Bild = eintrag.unter ? undefined : eintrag.symbol;
          const klasse = eintrag.unter ? 'seiten-rahmen-link seiten-rahmen-link--unter' : 'seiten-rahmen-link';
          // PF-411-2: die Marke steht nach dem Titel; Vorlesehilfen lesen „{Titel}, {label}“ statt der Zahl.
          const marke = eintrag.zaehler && eintrag.zaehler.wert > 0 ? eintrag.zaehler : null;
          const inhalt = <>{Bild && <Bild size={16} aria-hidden="true" className="seiten-rahmen-symbol" />}<span className="seiten-rahmen-text">{eintrag.titel}
            {marke && <><span className="seiten-rahmen-zaehler" aria-hidden="true">{marke.wert > 99 ? '99+' : marke.wert}</span>
              <span className="seiten-rahmen-nur-vorlesen">, {marke.label}</span></>}
            {eintrag.fund && <span className="seiten-rahmen-fund">{eintrag.fund}</span>}</span></>;
          return <li key={eintrag.key}>{eintrag.element ?? (eintrag.extern
            ? <Link to={eintrag.to} className={klasse}>{inhalt}<ArrowUpRight size={14} aria-hidden="true" /></Link>
            : <V to={eintrag.to} className={klasse} aria-current={eintrag.aktiv ? 'page' : undefined}>{inhalt}</V>)}</li>;
        })}</ul>}
      </div>;
    })}
  </nav>;
}

export default function SeitenRahmen({ klasse, beiEscape, suche, leisteZusatz, leisteName, gruppen, verweis = RouterVerweis, bereichsName, leisteFuss,
  inhaltAls = 'main', inhaltName, inhaltKlasse, inhaltRef, scroll = 'inhalt', unten, children }: SeitenRahmenProps) {
  const [text, setText] = useState('');
  const [offen, setOffen] = useState(false);
  const klappId = useId();
  const wurzel = useRef<HTMLDivElement>(null);
  const leiste = useRef<HTMLDivElement>(null);
  const klappKnopf = useRef<HTMLButtonElement>(null);
  const klappbar = useRef<HTMLDivElement>(null);
  const inhalt = useRef<HTMLElement | null>(null);
  const muster = suche ? text.trim() : '';
  const gezeigt = sichtbareGruppen(muster ? (suche?.filtern ? suche.filtern(muster) : titelFilter(gruppen, muster)) : gruppen);
  const keine = !!muster && !gezeigt.some(gruppe => gruppe.eintraege.length > 0);
  const aktiverEintrag = gruppen.flatMap(gruppe => gruppe.eintraege).find(eintrag => eintrag.aktiv);
  const aktiv = aktiverEintrag?.key ?? '';

  // Der Inhaltsbereich für den Aufrufer (Fokus auf die Überschrift) und für den Seitenanfang.
  const inhaltSetzen = useCallback((element: HTMLElement | null) => {
    inhalt.current = element;
    if (typeof inhaltRef === 'function') inhaltRef(element);
    else if (inhaltRef) (inhaltRef as React.MutableRefObject<HTMLElement | null>).current = element;
  }, [inhaltRef]);

  // GESTALTUNG.md 2.2 und 2.5: ein Wechsel beginnt oben im Inhalt (scrollt das Dokument, macht das der Aufrufer); die Leiste
  // rollt den offenen Eintrag in Sicht, ohne den Fokus zu verschieben.
  useEffect(() => { if (scroll === 'inhalt' && inhalt.current) inhalt.current.scrollTop = 0; }, [aktiv, scroll]);
  useEffect(() => {
    leiste.current?.querySelector<HTMLElement>('.seiten-rahmen-nav [aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [aktiv]);
  // Ein Wechsel klappt die schmale Leiste zu. Lag der Fokus darin (Klick auf einen Eintrag) oder ging er mit dem Eintrag
  // verloren, geht er auf den Knopf, statt ins Leere zu fallen. Setzt der Aufrufer danach selbst einen Fokus (etwa auf die
  // Überschrift), gilt seiner: seine Effekte laufen nach diesem.
  useEffect(() => {
    const fokus = document.activeElement;
    const verloren = !fokus || fokus === document.body;
    if (offen && (verloren || klappbar.current?.contains(fokus)) && window.matchMedia(SCHMAL).matches) klappKnopf.current?.focus();
    setOffen(false);
  }, [aktiv]); // eslint-disable-line react-hooks/exhaustive-deps

  // F-19: Escape auf der Seite ruft beiEscape des Aufrufers (seine Rückfrage, dann zurück). Am Dokument gehört, damit es auch
  // ohne Fokus in der Seite greift; was Escape schon verbraucht hat (Suchfeld, Klappe, Menüs, Dialoge), bleibt unberührt.
  const escapeRuf = useRef(beiEscape);
  escapeRuf.current = beiEscape;
  const mitEscape = !!beiEscape;
  useEffect(() => {
    if (!mitEscape) return;
    const taste = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing || dialogOffen(wurzel.current) || wurzel.current?.closest('[inert]')) return;
      event.preventDefault();
      escapeRuf.current?.();
    };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, [mitEscape]);

  // Enter öffnet den ersten Treffer über sein Element (so greift dessen Rückfrage); Escape leert zuerst den Text.
  const sucheTasten = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && text) { event.preventDefault(); event.stopPropagation(); setText(''); }
    else if (event.key === 'Enter') { event.preventDefault(); leiste.current?.querySelector<HTMLElement>('.seiten-rahmen-nav .seiten-rahmen-link')?.click(); }
  };
  // Escape klappt die aufgeklappte schmale Leiste wieder zu und gibt dem Knopf den Fokus.
  const leisteTasten = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !offen || event.defaultPrevented || !window.matchMedia(SCHMAL).matches) return;
    event.preventDefault(); event.stopPropagation(); setOffen(false); klappKnopf.current?.focus();
  };

  const Inhalt = inhaltAls;
  return <div ref={wurzel} className={klasse ? `seiten-rahmen ${klasse}` : 'seiten-rahmen'} data-scroll={scroll}>
    <div className="seiten-rahmen-leiste" ref={leiste} onKeyDown={leisteTasten}>
      <button ref={klappKnopf} type="button" className="seiten-rahmen-klappe" aria-expanded={offen} aria-controls={klappId}
        aria-label={aktiverEintrag ? `${leisteName}: ${aktiverEintrag.titel}` : leisteName} onClick={() => setOffen(wert => !wert)}>
        <span className="seiten-rahmen-text">{aktiverEintrag?.titel ?? leisteName}</span><ChevronDown size={16} aria-hidden="true" /></button>
      <div ref={klappbar} id={klappId} className="seiten-rahmen-klappbar" data-offen={String(offen)}>
        {suche && <div className="seiten-rahmen-suche" role="search"><Search size={16} aria-hidden="true" />
          <input type="search" aria-label={suche.label} placeholder={suche.platzhalter} value={text} autoComplete="off" spellCheck={false}
            onChange={event => setText(event.target.value)} onKeyDown={sucheTasten} /></div>}
        {leisteZusatz && <div className="seiten-rahmen-zusatz">{leisteZusatz}</div>}
        <LeistenGruppen gruppen={gezeigt} name={leisteName} verweis={verweis} />
        {suche && <p className="seiten-rahmen-leer" role="status">{keine ? suche.leerText : ''}</p>}
        {keine && suche?.leerWeg}
        {(bereichsName || leisteFuss) && <div className="seiten-rahmen-fuss">
          {bereichsName && <p className="seiten-rahmen-bereich">{bereichsName}</p>}
          {leisteFuss}
        </div>}
      </div>
    </div>
    <Inhalt ref={inhaltSetzen} aria-label={inhaltAls === 'main' ? inhaltName : undefined} className={inhaltKlasse ? `seiten-rahmen-inhalt ${inhaltKlasse}` : 'seiten-rahmen-inhalt'}>
      {children}
    </Inhalt>
    {unten && <div className="seiten-rahmen-unten">{unten}</div>}
  </div>;
}
